package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.Strings
import com.ilussobsa.sdk.*
import com.ilussobsa.utils.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogPageNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import com.lightningkite.serialization.*


fun ViewWriter.vehicleDetail(
    selectedLot: UUID,
    immutable: Boolean = false,
    goto: suspend (vehicleId: UUID?) -> Unit,
    backButtonInfo: BackButtonInfo? = null
): ViewModifiable {
    val selected = remember { currentSession.awaitNotNull().vehicle.get(selectedLot)() }
    val notify = vehicleRelationshipUpsertProp(selectedLot, VehicleRelationship.path.notifications)

    return scrolls -col {
        card  - col {
            row {
                backButtonIfNeeded(backButtonInfo)

                centered - expanding - h2 { ::content.invoke { selected.awaitNotNull().mmt } }
                align(Align.End, Align.Start) - row {
                    ::shown {
                        val sel = selected()
                        !isSeller() && sel?.liveAt == null
                    }
                    compact - iconButton - toggleButton {
                        icon {
                            ::source { Icon.notifications2 }
                            description = Strings.notificationsEnabled
                        }
                        checked bind notify
                    }
                }
            }
            align(Align.Start, Align.Center) - sellingIssueIcons(selected.waitForNotNull, 1.5.rem)
            vehicleInfo(selectedLot, immutable, goto)
        }
    }
}


private data class SellingExtraInfoPlus(
    val icon: Icon,
    val text: String,
    val getInfo: ExtraInfo
)

fun ViewWriter.sellingIssueIcons(selected: Reactive<Vehicle>, size: Dimension = 2.rem) {

    compact - row {
        forEachUpdating(remember {
            buildList<SellingExtraInfoPlus> {
                val v = selected.awaitNotNull()
                v.completelyClean?.let { add(SellingExtraInfoPlus(Icon.clean, Strings.completelyClean, it)) }
                if (v.transmission == Transmission.Manual) {
                    add(
                        SellingExtraInfoPlus(
                            Icon.manual,
                            Strings.manualTransmission,
                            ExtraInfo(Strings.thisVehicleHasAManualTransmission)
                        )
                    )
                }
                v.priorAccident?.let {
                    add(
                        SellingExtraInfoPlus(
                            Icon.priorAccident,
                            Strings.priorAccidentReported,
                            it
                        )
                    )
                }
                if (v.fuelType == FuelType.Electric) {
                    add(
                        SellingExtraInfoPlus(
                            Icon.electric,
                            Strings.electric,
                            ExtraInfo(Strings.thisIsAnElectricVehicle)
                        )
                    )
                }
                v.paintwork?.let { add(SellingExtraInfoPlus(Icon.paintwork, Strings.paintWork, it)) }
                v.warningLights?.let { add(SellingExtraInfoPlus(Icon.warning, Strings.warningLights, it)) }
                v.towRequired?.let { add(SellingExtraInfoPlus(Icon.towing, Strings.towRequired, it)) }
                v.nonRunner?.let { add(SellingExtraInfoPlus(Icon.close, Strings.nonRunner, it)) }
                v.structuralDamage?.let { add(SellingExtraInfoPlus(Icon.damage, Strings.structuralDamage, it)) }
                v.airConditioningIssue?.let {
                    add(
                        SellingExtraInfoPlus(
                            Icon.acIssues,
                            Strings.airConditioningIssue,
                            it
                        )
                    )
                }
                v.transmissionIssue?.let { add(SellingExtraInfoPlus(Icon.close, Strings.transmissionIssue, it)) }
                v.odometerIssue?.let { add(SellingExtraInfoPlus(Icon.speedometer, Strings.odometerIssue, it)) }
                v.canadian?.let { add(SellingExtraInfoPlus(Icon.canada, Strings.canadian, it)) }
                // v.titleNotPresent?.let { add(SellingExtraInfoPlus(Icon.missingTitle, Strings.titleNotPresent, it)) }
                v.salvage?.let { add(SellingExtraInfoPlus(Icon.recycling, Strings.salvage, it)) }
                v.lemonLaw?.let { add(SellingExtraInfoPlus(Icon.lemon, Strings.lemonLaw, it)) }
                v.flood?.let { add(SellingExtraInfoPlus(Icon.waterDrop, Strings.floodDamage, it)) }
                v.stolenOrRecovery?.let { add(SellingExtraInfoPlus(Icon.money, Strings.stolenRecovery, it)) }
                v.rentalOrTaxi?.let { add(SellingExtraInfoPlus(Icon.taxi, Strings.rentalTaxi, it)) }
                v.trueMileageUnknown?.let { add(SellingExtraInfoPlus(Icon.close, Strings.trueMileageUnknown, it)) }
            }
        }, 0) { extraInfo ->
            hintPopover(preferredDirection = PopoverPreferredDirection.belowCenter) {
                col {
                    h3 { ::content{ extraInfo().text } }
//                    text { ::content { extraInfo.await().getInfo.description ?: Strings.noAdditionalInformationGiven } }
                }
            }
            sizeConstraints(width = size, height = size)

            icon {
                ::source { extraInfo().icon }
                ::description { extraInfo().text }
            }
        }
    }
}

fun ViewWriter.sellingLotShortDetails(it: Reactive<Vehicle>) {
    row {
        centered - sizeConstraints(
            width = 7.5.rem,
            height = 5.rem
        ) - tweakTheme { it.copy(cornerRadii = CornerRadii.Constant(0.75.rem)) } - image {
            this.description = ""
            ::source { it().thumbnail?.let { ImageRemote(it.location) } }
            scaleType = ImageScaleType.Crop
        }
        space()
        centered - expanding - compact - col {
            row {
                centered - sizeConstraints(1.5.rem, 1.5.rem) - icon {
                    ::shown{ isSeller() }
                    source = Icon.sell
                } in hintPopover(preferredDirection = PopoverPreferredDirection.aboveRight) {
                    col {
                        centered - text {
                            ::content{ Strings.thisIsAVehicleYouAreSelling }
                        }
                    }
                }
//
                centered - text {
                    ::content { it().run { "${year ?: ""} ${make ?: ""} ${model ?: ""}" } }
                }

            }

            subtext { ::content { it().statusDescription() } }
            row {
                centered - subtext {
                    ::content {
                        it().odometer?.toCommaString()?.plus(" mi")
                            ?: Strings.noMileageInformation
                    }
                }
//                sellingIssueIcons(it, size = 1.rem)
            }
//            sellingShortSellerLine(remember { dealerships().get(it.await().seller).awaitNotNull() })
        }
    }
}

fun <V> ViewWriter.vehicleRelationshipUpsertProp(selectedLot: UUID, key: DataClassPath<VehicleRelationship, V>): MutableReactive<V> {
    val basis = remember {
        currentUserSessionNullable()?.vehicleRelationship?.get(
            UserVehiclePair(
                currentUser()?._id ?: return@remember null, selectedLot
            )
        )
    }
    val flat = remember { basis()?.invoke() }
    val default =
        VehicleRelationship(UserVehiclePair(nullUuid, nullUuid), Vehicle(vin = "").short())
    return remember<V> { flat()?.let { key.get(it) } as V }
        .withWrite { value -> vehicleRelationshipUpsert(selectedLot, modification { key.assign(value) }) }
}

suspend fun ViewWriter.vehicleRelationshipUpsert(selectedLot: UUID, update: Modification<VehicleRelationship>) {
    fun promptVerification() = dialogPageNavigator.navigate(VerificationDialog())

    val userSession = currentUserSessionNullable() ?: run { promptVerification(); return }
    val user = currentUser() ?: run { promptVerification(); return }

    val existing =
        userSession.vehicleRelationship[UserVehiclePair(user._id, selectedLot)]()
    if (existing == null) {
        userSession.vehicleRelationship.add(
            VehicleRelationship(
                UserVehiclePair(user._id, selectedLot),
                vehicleDenormalizedInfo = userSession.vehicle[selectedLot].awaitNotNull().short(),
                firstInteraction = now(),
            ).let { update(it) }
        )
    } else {
        userSession.vehicleRelationship.get(
            UserVehiclePair(user._id, selectedLot)
        ).modify(update)
    }
}